// Hotspots screen (`/hotspots`) — README §6.5.
//
// Lists both declared hotspots (from prerequisites.hotspots[]) and any
// function-surfaced "computed" hotspots from the latest anchored plan.
// Heads-up only: no checkboxes. Each row shows WHEN the hotspot lands — a
// single time, or a "from → to" range when it spans several tasks — instead
// of raw task references. The times come from the same anchor Preview uses:
// the real anchored schedule during the cook, else the Planner's serve time,
// else the present moment (so a time always shows).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Hotspot } from '@cookchow/shared';
import { Button, Wordmark } from '../design/components';
import { theme } from '../design/theme';
import { formatHHMM } from '../cooking/helpers';
import { buildTaskStartMap } from '../cooking/previewProjection';
import { useAppStore } from '../state/store';

interface Row {
  id: string;
  /** Display time: a single "16:31", a "16:31 → 16:39" range, or null. */
  time: string | null;
  text: string;
}

// Resolve a hotspot's taskRefs to a display time via the taskId → startMs map:
// the earliest referenced start, plus the latest as a range when they differ.
function timeForRefs(taskRefs: readonly string[], startMs: Map<string, number>): string | null {
  const times = taskRefs.map((ref) => startMs.get(ref)).filter((ms): ms is number => ms != null);
  if (times.length === 0) return null;
  const min = Math.min(...times);
  const max = Math.max(...times);
  const from = formatHHMM(new Date(min).toISOString());
  if (min === max) return from;
  return `${from} → ${formatHHMM(new Date(max).toISOString())}`;
}

export default function Hotspots() {
  const navigate = useNavigate();
  const recipe = useAppStore((s) => s.recipe);
  const anchoredPlan = useAppStore((s) => s.anchoredPlan);
  const serveTime = useAppStore((s) => s.serveTime);
  const markVisited = useAppStore((s) => s.markVisited);

  // Capture "now" once on mount — the times are a static read-out, not a
  // ticking clock (mirrors the Preview/Planner read-outs).
  const [displayStart] = useState(() => Date.now());

  useEffect(() => {
    markVisited('hotspots');
  }, [markVisited]);

  // taskId → start (epoch ms). During the cook the real anchored schedule is
  // authoritative; before it, project under the Planner's serve time, else
  // the present moment — the same anchor Preview uses, so the numbers match.
  const startMs = useMemo(() => {
    if (!recipe) return new Map<string, number>();
    if (anchoredPlan?.schedule?.length) {
      const m = new Map<string, number>();
      for (const s of anchoredPlan.schedule) {
        const ms = Date.parse(s.startAt);
        if (Number.isFinite(ms)) m.set(s.id, ms);
      }
      return m;
    }
    return buildTaskStartMap(recipe, serveTime, displayStart);
  }, [recipe, anchoredPlan, serveTime, displayStart]);

  if (!recipe) {
    return (
      <main style={{ padding: '1rem' }}>
        <Wordmark size="md" as="h1" />
        <p style={{ color: theme.text.muted }}>Pick a recipe first.</p>
        <Button onClick={() => navigate('/')}>Back to Home</Button>
      </main>
    );
  }

  const declared = recipe.cookpit.prerequisites?.hotspots ?? [];

  // Use the anchored plan as the authoritative source whenever present:
  // it carries both declared (with verbatim text + taskRefs) and computed
  // hotspots in the same shape.
  const planSource: Hotspot[] = anchoredPlan?.hotspots ?? [];

  const rows: Row[] = (
    planSource.length
      ? planSource.map((h) => ({ id: h.id, text: h.text, taskRefs: h.taskRefs }))
      : declared.map((h) => ({ id: h.id, text: h.text, taskRefs: h.taskRefs ?? [] }))
  ).map((h) => ({ id: h.id, text: h.text, time: timeForRefs(h.taskRefs, startMs) }));

  return (
    <main
      style={{
        padding: '1rem',
        background: theme.bg.base,
        color: theme.text.primary,
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        maxWidth: '40rem',
        margin: '0 auto',
        minHeight: '100vh',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <Wordmark size="sm" as="span" />
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Hotspots — where it gets busy.</h1>
      </header>

      {rows.length === 0 && (
        <p style={{ color: theme.text.muted }}>No hotspots declared on this recipe.</p>
      )}

      <ol
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        {rows.map((row) => (
          <li
            key={row.id}
            style={{
              padding: '0.75rem 1rem',
              background: theme.bg.raised,
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
              {row.time && (
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                    color: theme.text.primary,
                  }}
                >
                  {row.time}
                </span>
              )}
              <span style={{ fontWeight: 500 }}>{row.text}</span>
            </div>
          </li>
        ))}
      </ol>

      <div>
        <Button variant="secondary" onClick={() => navigate('/prereqs')}>
          Back to Prerequisites
        </Button>
      </div>
    </main>
  );
}
